import { apiRequest } from './client';
import type {
  PatientPointsSummary,
  PointsEntry,
  PointsEntryList,
  PointsReward,
  PointsRewardType,
} from '../types/admin';

// --- reward catalog --------------------------------------------------------

export type CreatePointsRewardPayload = {
  readonly name_uk: string;
  readonly name_en: string;
  readonly point_cost: number;
  readonly reward_type: PointsRewardType;
};

export type UpdatePointsRewardPayload = {
  readonly name_uk?: string;
  readonly name_en?: string;
  readonly point_cost?: number;
  readonly reward_type?: PointsRewardType;
  readonly is_active?: boolean;
};

export function listPointsRewards(
  accessToken: string,
  includeInactive = false,
): Promise<readonly PointsReward[]> {
  const query = includeInactive ? '?include_inactive=true' : '';
  return apiRequest<readonly PointsReward[]>(`/admin/points-rewards${query}`, {
    token: accessToken,
  });
}

export function createPointsReward(
  payload: CreatePointsRewardPayload,
  accessToken: string,
): Promise<PointsReward> {
  return apiRequest<PointsReward>('/admin/points-rewards', {
    method: 'POST',
    body: payload,
    token: accessToken,
  });
}

export function updatePointsReward(
  id: string,
  payload: UpdatePointsRewardPayload,
  accessToken: string,
): Promise<PointsReward> {
  return apiRequest<PointsReward>(`/admin/points-rewards/${id}`, {
    method: 'PATCH',
    body: payload,
    token: accessToken,
  });
}

export function deactivatePointsReward(id: string, accessToken: string): Promise<void> {
  return apiRequest<void>(`/admin/points-rewards/${id}`, {
    method: 'DELETE',
    token: accessToken,
  });
}

// --- patient ledger --------------------------------------------------------

export type GrantPointsPayload = {
  readonly points: number;
  readonly note?: string | null;
};

/**
 * Either `reward_id` (cost comes from the catalog) or `points` (free-form).
 * Passing both means "this reward, at a price the admin is overriding".
 */
export type RedeemPointsPayload = {
  readonly reward_id?: string | null;
  readonly points?: number | null;
  readonly note?: string | null;
};

export function getPatientPoints(
  patientId: string,
  accessToken: string,
): Promise<PatientPointsSummary> {
  return apiRequest<PatientPointsSummary>(`/admin/patients/${patientId}/points`, {
    token: accessToken,
  });
}

export function listPatientPointsEntries(
  patientId: string,
  params: { readonly page: number; readonly pageSize: number },
  accessToken: string,
): Promise<PointsEntryList> {
  const query = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.pageSize),
  });
  return apiRequest<PointsEntryList>(
    `/admin/patients/${patientId}/points/entries?${query.toString()}`,
    { token: accessToken },
  );
}

export function grantPoints(
  patientId: string,
  payload: GrantPointsPayload,
  accessToken: string,
): Promise<PointsEntry> {
  return apiRequest<PointsEntry>(`/admin/patients/${patientId}/points/grants`, {
    method: 'POST',
    body: payload,
    token: accessToken,
  });
}

export function redeemPoints(
  patientId: string,
  payload: RedeemPointsPayload,
  accessToken: string,
): Promise<PointsEntry> {
  return apiRequest<PointsEntry>(`/admin/patients/${patientId}/points/redemptions`, {
    method: 'POST',
    body: payload,
    token: accessToken,
  });
}

export function reversePointsEntry(
  patientId: string,
  entryId: string,
  payload: { readonly note?: string | null },
  accessToken: string,
): Promise<PointsEntry> {
  return apiRequest<PointsEntry>(
    `/admin/patients/${patientId}/points/entries/${entryId}/reverse`,
    {
      method: 'POST',
      body: payload,
      token: accessToken,
    },
  );
}
